/**
 * The footer every controlled document carries.
 *
 * "LBL100523 Rev. 01 · Company Confidential · Page 1" is not decoration. A procedure is read one
 * page at a time, photocopied, and pinned up beside a machine; a page that does not say WHICH
 * document and WHICH REVISION it belongs to is a page nobody can trust, and every quality system
 * requires the identity on every sheet.
 *
 * IT IS NOT PART OF THE BODY, and that is the whole design. The number and the revision are facts
 * QMS owns: a revision is raised, a document is released, and the footer changes without a word of
 * the text changing. Typed into the body they would be a copy that goes stale the first time the
 * document is revised -- which is exactly how a procedure ends up printed with last year's revision
 * on it. So the footer is drawn by the page, from the record, and is not editable.
 *
 * ON SCREEN IT APPEARS ONCE, at the foot of the sheet. In print it repeats on every page, because
 * position: fixed inside a print context is painted into each page's margin by the browser.
 *
 * THE PAGE NUMBER IS THE PART THAT IS NOT HERE, and it cannot be: a page number needs pagination,
 * and this editor is deliberately scrolled rather than paginated -- a page break drawn at a guessed
 * height would be wrong exactly when somebody relied on it. In print, CSS can count pages only
 * inside `@page` margin boxes, which no browser implements. Numbering belongs to the rendition, the
 * server-side render that produces the PDF that gets signed, and that is where it will come from.
 */
import React from "react";
import { Box } from "@mui/material";

export type FooterRecord = {
    ObjectID?: string | null;
    /** The revision's title. The automatic footer does not print it; a written header does. */
    Name?: string | null;
    DisplayRevision?: string | null;
};

export default function DocumentFooter({ record, accountName, status }: {
    record?: FooterRecord | null;
    /** The tenant's own name, for the confidentiality marking. */
    accountName?: string | null;
    status?: string | null;
}) {
    // NOTHING RATHER THAN A FOOTER OF BLANKS. Before the record arrives there is no identity to
    // print, and a rule with empty cells under every document reads as a rendering fault.
    if (!record?.ObjectID) return null;

    const left = record.DisplayRevision
        ? `${record.ObjectID} Rev. ${record.DisplayRevision}`
        : record.ObjectID;

    // THE TENANT'S OWN NAME, not an invented claim. "Company Confidential" asserts something about
    // somebody else's document; naming the account states a fact. Which marking a document carries
    // is properly a template setting -- see the note in DocumentPaper about templates -- and this
    // is the honest default until there is somewhere to configure it.
    const centre = accountName ? `${accountName} Confidential` : "Confidential";

    // A DRAFT SAYS SO ON EVERY PAGE. An unreleased procedure that looks released when printed is
    // the single most dangerous thing a document system can emit, and the status is exactly the
    // fact the body cannot carry.
    const right = status && status !== "RELEASED" ? status : "";

    return (
        <Box className="parkour-footer" aria-hidden="true" sx={{
            display: "flex", alignItems: "baseline", gap: 2,
            borderTop: "1px solid #000000", pt: "4pt",
            // THE SAME PLACEMENT THE WRITTEN FOOTER GETS, from the same page setup: the automatic
            // footer and one the author wrote must sit in the same place or changing from one to
            // the other would move the line.
            mt: "calc(1in - var(--parkour-footer-margin, 0.5in))",
            mb: "calc(var(--parkour-footer-margin, 0.5in) - 1in)",
            fontFamily: "Arial, Helvetica, sans-serif", fontSize: "8pt", color: "#202124",
            // NOT SELECTABLE AND NOT EDITABLE. It sits inside the page so it prints with it, and a
            // caret must never land in it -- it is not the author's text.
            userSelect: "none",
        }}>
            <Box sx={{ flex: 1, textAlign: "left" }}>{left}</Box>
            <Box sx={{ flex: 1, textAlign: "center" }}>{centre}</Box>
            <Box sx={{ flex: 1, textAlign: "right", fontWeight: right ? 700 : 400 }}>{right}</Box>
        </Box>
    );
}
